/**
 * Composable that fetches entity lists and deeplink type metadata from the API.
 *
 * Both `Buttons.vue` and `DeepLink.vue` use this composable instead of each
 * fetching the same eight endpoints independently. The entity lists are
 * normalised to `NameIdentifiable[]` so the UI pickers can treat them uniformly.
 *
 * Usage:
 * ```ts
 * const { load, getSource, deeplinkTypes } = useDeeplinkEntities()
 * load() // fetches all lists
 * getSource('showMan') // => NameIdentifiable[] for the entity picker
 * ```
 */

import { ref } from 'vue'

import { GalleryService } from '@/pages/gallery/gallery.service'
import { type GalleryItem } from '@/pages/gallery/gallery.type'
import { GuestsService } from '@/pages/guests/guests.service'
import { type GuestType } from '@/pages/guestTypes/GuestType.model'
import { GuestTypeService } from '@/pages/guestTypes/guestType.service'
import { InfosService } from '@/pages/infos/infos.service'
import { DeeplinkService } from '@/pages/notifications/deeplinks.service'
import { ProgEventService } from '@/pages/progEvent/progEvent.service'
import { ProgramsService } from '@/pages/programs/programs.service'
import { type MusicGroup } from '@/shared/types/musicGroup/musicGroup.type'
import { type ProgEvent } from '@/shared/types/progEvent.type'
import { type Program } from '@/shared/types/program.type'

import { type StateInfo } from '../../pages/infos/infos.store'
import { MapService } from '../../pages/map/map.service'
import { type MapObject } from '../../pages/map/map.type'
import { type EntitySource } from './deeplink-targets.config'

/** An entity that can be displayed in a picker dropdown. */
export type NameIdentifiable = { _id: string; name: string }

/** Wraps any object with an optional `_id` into the picker-friendly format. */
function toNameIdentifiable<T extends { _id?: string }>(
  item: T,
  name: string,
): NameIdentifiable {
  return { _id: item._id ?? '', name }
}

const showmen = ref<NameIdentifiable[]>([])
const maps = ref<NameIdentifiable[]>([])
const programs = ref<NameIdentifiable[]>([])
const shows = ref<NameIdentifiable[]>([])
const infos = ref<NameIdentifiable[]>([])
const gallery = ref<NameIdentifiable[]>([])
const guestTypes = ref<NameIdentifiable[]>([])
const deeplinkTypes = ref<{ text: string; name: string }[]>([])

const loading = ref(false)
let loaded = false
let loadPromise: Promise<void> | null = null

/** Maps EntitySource keys to their corresponding reactive list. */
const entities: Record<EntitySource, typeof showmen> = {
  showmen,
  maps,
  programs,
  shows,
  infos,
  gallery,
  guestTypes,
}

/**
 * Reactive entity lists and helpers, fetched once from the API.
 *
 * All HTTP calls run in parallel via `Promise.all`. Subsequent calls to
 * `load()` are no-ops once data has been fetched.
 */
export function useDeeplinkEntities() {
  /** Fetches all entity lists and deeplink types in parallel. Safe to call multiple times. */
  async function load(): Promise<void> {
    if (loaded) return
    if (loadPromise) return loadPromise

    loading.value = true

    loadPromise = (async () => {
      const [
        programsData,
        showmenData,
        showsData,
        mapsData,
        infosData,
        galleryData,
        typesData,
        guestTypesData,
      ] = await Promise.all([
        ProgramsService.getAll().catch(() => [] as Program[]),
        GuestsService.getAll().catch(() => [] as MusicGroup[]),
        ProgEventService.getAll().catch(() => [] as ProgEvent[]),
        MapService.getMapList().catch(() => [] as MapObject[]),
        InfosService.getAll().catch(() => [] as StateInfo[]),
        GalleryService.getAll().catch(() => [] as GalleryItem[]),
        DeeplinkService.getAllTypes().catch(() => []),
        GuestTypeService.getAll().catch(() => [] as GuestType[]),
      ])

      programs.value = programsData
        .filter((p: Program) => p.names)
        .map((p: Program) => toNameIdentifiable(p, p.names.fr))

      showmen.value = showmenData
        .filter((g: MusicGroup) => g.showArtist)
        .map((g: MusicGroup) => toNameIdentifiable(g, g.names.fr))

      shows.value = showsData
        .filter((e: ProgEvent) => e.isVisible)
        .map((e: ProgEvent) =>
          toNameIdentifiable(
            e,
            e.titles?.fr ??
              e.musicGroups?.map((g: MusicGroup) => g.names.fr).join(', ') ??
              '',
          ),
        )

      maps.value = mapsData.map((m: MapObject) =>
        toNameIdentifiable(m, m.names.fr),
      )

      infos.value = infosData
        .filter((i: StateInfo) => i.showInfoPratique)
        .map((i: StateInfo) => toNameIdentifiable(i, i.names.fr))

      gallery.value = galleryData.map((s: GalleryItem) =>
        toNameIdentifiable(s, s.titles.fr),
      )

      guestTypes.value = guestTypesData.map((gt: GuestType) =>
        toNameIdentifiable(gt, gt.names.fr),
      )

      deeplinkTypes.value = typesData
      loaded = true
      loading.value = false
      loadPromise = null
    })()

    return loadPromise
  }

  /**
   * Returns the entity picker items for a given deeplink base.
   * Callers resolve a base to its source via `getEntitySource()` before
   * calling this. Bases without an entity source (e.g. `'home'`, `'webview'`)
   * pass `undefined` and get `[]`.
   */
  function getSource(source: EntitySource | undefined): NameIdentifiable[] {
    return source ? entities[source].value : []
  }

  return {
    showmen,
    maps,
    programs,
    shows,
    infos,
    gallery,
    guestTypes,
    deeplinkTypes,
    loading,
    load,
    getSource,
  }
}
